/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: InboundRoutesPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for Inbound Routes management with list view.
    Displays inbound routing rules for incoming calls.

 🔧 Notes:
    - Domain-scoped data
    - Supports pagination and search
    - Includes delete confirmation

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownLeft, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNavigationStore } from '../../store/navigation';
import {
  deleteInboundRouteById,
  navigateToInboundRouteDuplicate,
} from './inboundRoutesActions';
import { useInboundRouteList } from './hooks/useInboundRouteList';
import { InboundRoute } from './types';
import { useDestinationOptions, resolveDestinationLabel } from '../../hooks/useDestinationOptions';


const ACTION_BUTTON_CLASSES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
} as const;

export default function InboundRoutesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    route: InboundRoute | null;
  }>({
    isOpen: false,
    route: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const pageTitle = useMemo(() => t('inboundRoutes:title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);


  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useInboundRouteList();

  // Preload unique destination modules into cache
  const uniqueModules = useMemo(() => {
    const modules = new Set<string>();
    data.forEach((item) => {
      if (item.destinationModule) modules.add(item.destinationModule);
    });
    return Array.from(modules).filter((m) => m !== 'terminate_call');
  }, [data]);

  useDestinationOptions(uniqueModules[0] ?? '');
  useDestinationOptions(uniqueModules[1] ?? '');
  useDestinationOptions(uniqueModules[2] ?? '');
  useDestinationOptions(uniqueModules[3] ?? '');
  useDestinationOptions(uniqueModules[4] ?? '');
  useDestinationOptions(uniqueModules[5] ?? '');

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'name') {
          return {
            ...column,
            render: (_value: unknown, item: InboundRoute) => (
              <span className="font-medium text-foreground">{item.name}</span>
            )
          };
        }
        if (column.key === 'numberPattern') {
          return {
            ...column,
            render: (_value: unknown, item: InboundRoute) => (
              <span className="font-mono font-medium text-primary">
                {item.numberPattern || '—'}
              </span>
            )
          };
        }
        if (column.key === 'destinationModule') {
          return {
            ...column,
            render: (_value: unknown, item: InboundRoute) => {
              const moduleLabel = item.destinationModule
                ? t(`outboundRoutes.modules.${item.destinationModule}.label`, {
                    defaultValue: item.destinationModule
                  })
                : '—';

              let destinationLabel: string = item.destinationData || '';
              if (item.destinationData && item.destinationModule) {
                if (item.destinationModule === 'terminate_call') {
                  destinationLabel = t(
                    `outboundRoutes.terminateCauses.${item.destinationData}.label`,
                    { defaultValue: item.destinationData }
                  );
                } else {
                  destinationLabel =
                    resolveDestinationLabel(item.destinationModule, item.destinationData)
                    ?? item.destinationData;
                }
              }

              return (
                <span className="text-sm text-foreground">
                  {moduleLabel}
                  {item.destinationData ? ` • ${destinationLabel}` : ''}
                </span>
              );
            }
          };
        }
        if (column.key === 'priority') {
          return {
            ...column,
            render: (_value: unknown, item: InboundRoute) => (
              <span className="text-sm font-medium text-foreground">
                {item.priority ?? 100}
              </span>
            )
          };
        }
        if (column.key === 'enabled') {
          return {
            ...column,
            render: (_value: unknown, item: InboundRoute) => (
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  item.enabled
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}
              >
                {item.enabled ? t('common.enabled') : t('common.disabled')}
              </span>
            )
          };
        }
        return column;
      }),
    [baseColumns, t]
  );

  const handleAdd = useCallback(() => {
    navigate('/pbx/calls-routing/inbound/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (route: InboundRoute) => {
      navigate(`/pbx/calls-routing/inbound/${route.uuid ?? route.id}`);
    },
    [navigate],
  );

  const handleDuplicate = useCallback(
    (route: InboundRoute) => {
      navigateToInboundRouteDuplicate(navigate, route.uuid ?? route.id);
    },
    [navigate],
  );

  const handleDeleteClick = useCallback((route: InboundRoute) => {
    setDeleteModal({ isOpen: true, route });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.route) return;

    setIsDeleting(true);
    try {
      await deleteInboundRouteById(deleteModal.route.uuid ?? deleteModal.route.id);

      showSuccess(t('inboundRoutes:messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, route: null });
      refresh();
    } catch (error) {
      const msg = error instanceof Error && error.message !== 'DELETE_FAILED'
        ? error.message
        : t('inboundRoutes:messages.deleteError');
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.route, refresh, showError, showSuccess, t]);

  const renderActions = useCallback(
    (route: InboundRoute) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(route)}
            className={`${ACTION_BUTTON_CLASSES.base} ${ACTION_BUTTON_CLASSES.edit}`}
            aria-label={t('common.edit')}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(route)}
            className={`${ACTION_BUTTON_CLASSES.base} ${ACTION_BUTTON_CLASSES.duplicate}`}
            aria-label={t('common.duplicate')}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(route)}
            className={`${ACTION_BUTTON_CLASSES.base} ${ACTION_BUTTON_CLASSES.delete}`}
            aria-label={t('common.delete')}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={handleAdd}
          title={pageTitle}
          titleIcon={<ArrowDownLeft className="h-5 w-5 text-primary" />}
          addButtonText={t('inboundRoutes:actions.add')}
          renderActions={renderActions}
          searchPlaceholder={t('inboundRoutes:searchPlaceholder')}
          emptyMessage={t('inboundRoutes:emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, route: null })}
        onConfirm={handleDeleteConfirm}
        title={t('inboundRoutes:messages.deleteTitle')}
        message={t('inboundRoutes:messages.confirmDelete', {
          name: deleteModal.route?.name || '',
        })}
        confirmText={t('inboundRoutes:actions.delete')}
        cancelText={t('inboundRoutes:actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

